/**
 * useSalesReps — canonical sales-rep roster reader.
 *
 * OWNER-TEST-BLOCKER-FIX-PACK (2026-02):
 * Legacy `/api/sales-reps` surface has been retired (HTTP 410). This hook
 * now reads the canonical `clinic_memberships` projection via the RBAC
 * users API: `GET /api/admin/users?clinic_id={cid}&role=provider_sales_rep`.
 *
 * No CUD methods are exposed — sales rep onboarding is exclusively via
 * the canonical Team Invitations flow (POST /api/auth/unified/invite).
 */
import { useState, useCallback } from 'react';
import api from '../utils/api';

function normalizeRep(user) {
  if (!user) return null;
  const uid = user.user_id || user.id;
  return {
    id: uid,
    rep_id: uid,
    user_id: uid,
    name: user.name || user.email,
    email: user.email,
    role: 'provider_sales_rep',
    avatar:
      user.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email || '?')}&background=random`,
    status: user.status || 'active',
    languages: [],
    specializations: [],
    createdAt: user.created_at,
    updatedAt: user.updated_at,
  };
}

export function useSalesReps(clinicId) {
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSalesReps = useCallback(
    async (overrideClinicId) => {
      const cid = overrideClinicId || clinicId;
      if (!cid) return [];
      setLoading(true);
      try {
        const res = await api.get(
          `/admin/users?clinic_id=${encodeURIComponent(cid)}&role=provider_sales_rep&limit=200`,
        );
        const users = res?.data?.users || [];
        const normalized = users
          .filter((u) => u.status !== 'deactivated')
          .map(normalizeRep);
        setSalesReps(normalized);
        return normalized;
      } catch {
        // Quiet failure — the canonical roster surface is auth-gated and
        // may return 401 / 403 for non-clinic roles. Callers fall back
        // to an empty list (their UI already handles that state).
        setSalesReps([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [clinicId],
  );

  return { salesReps, loading, fetchSalesReps };
}

export default useSalesReps;
